// Task 24594 — notes against a leave request. "useful for things like sickness
// updates".
//
// A sickness absence is not one event, it is a running story: a fit note
// arrives, a return date moves, the absence gets extended. None of that belongs
// in the request's own Notes field, which is what the staff member wrote when
// they raised it and should stay as they wrote it. So this is a separate
// thread, newest first, each entry stamped with who wrote it and when.
//
// Calls the API directly rather than going through redux, which is the pattern
// every other note thread in the app follows (see CustomerAssetDetails) — and
// here it also keeps the modal from touching the redux lists the screens behind
// it are reading.

import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Label } from "reactstrap";
import { toast } from "react-toastify";

import LoadingSpinner from "../../Common/Spinner";
import HandleDateTime from "../../Common/HandleDateTime";
import {
  createStaffLeaveNoteApi,
  deleteStaffLeaveNoteApi,
  searchStaffLeaveNoteApi,
  updateStaffLeaveNoteApi,
} from "../../../helpers/fakebackend_helper";

const emptyDraft = { Title: "", Description: "" };

const LeaveRequestNotes = ({ leaveId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  // Which note is being edited, and the values being edited into it.
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyDraft);

  const load = useCallback(async () => {
    if (!leaveId) return;
    setLoading(true);
    try {
      const response = await searchStaffLeaveNoteApi([
        { staffleavenote_data: { StaffLeave_Id: leaveId } },
      ]);
      if (response?.status === 200) {
        setNotes(response.staffleavenote_list || []);
      } else {
        toast.error(response?.message || "Could not load notes");
      }
    } catch (e) {
      toast.error("Could not load notes");
    } finally {
      setLoading(false);
    }
  }, [leaveId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    // The API refuses an empty note too; catching it here saves the round trip
    // and gives a message that names the box rather than the column.
    if (!draft.Title.trim() && !draft.Description.trim()) {
      toast.error("Write something first");
      return;
    }
    setSaving(true);
    try {
      const response = await createStaffLeaveNoteApi([
        {
          StaffLeave_Id: leaveId,
          Title: draft.Title.trim(),
          Description: draft.Description.trim(),
        },
      ]);
      if (response?.status === 200) {
        setDraft(emptyDraft);
        await load();
      } else {
        toast.error(response?.message || "Could not add the note");
      }
    } catch (e) {
      toast.error("Could not add the note");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editDraft.Title.trim() && !editDraft.Description.trim()) {
      toast.error("A note needs a title or a description");
      return;
    }
    setSaving(true);
    try {
      const response = await updateStaffLeaveNoteApi([
        {
          Id: editingId,
          Title: editDraft.Title.trim(),
          Description: editDraft.Description.trim(),
        },
      ]);
      if (response?.status === 200) {
        setEditingId(null);
        await load();
      } else {
        toast.error(response?.message || "Could not save the note");
      }
    } catch (e) {
      toast.error("Could not save the note");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    // Deleting somebody's written record is worth one question. The row is soft
    // deleted server-side, but nothing in this UI brings it back.
    if (!window.confirm("Delete this note?")) return;
    setSaving(true);
    try {
      const response = await deleteStaffLeaveNoteApi([id]);
      if (response?.status === 200) {
        await load();
      } else {
        toast.error(response?.message || "Could not delete the note");
      }
    } catch (e) {
      toast.error("Could not delete the note");
    } finally {
      setSaving(false);
    }
  };

  if (!leaveId) {
    return (
      <p className="text-muted mb-0">
        Save the request first and notes can be added to it.
      </p>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <Label className="fw-semibold">Add a note</Label>
        <Input
          className="mb-2"
          placeholder="Short title, e.g. Fit note received"
          value={draft.Title}
          onChange={(e) => setDraft({ ...draft, Title: e.target.value })}
        />
        <Input
          type="textarea"
          rows={2}
          placeholder="What has changed?"
          value={draft.Description}
          onChange={(e) => setDraft({ ...draft, Description: e.target.value })}
        />
        <div className="d-flex justify-content-end mt-2">
          <Button color="success" size="sm" onClick={handleAdd} disabled={saving}>
            <i className="ri-add-line align-middle me-1" />
            Add note
          </Button>
        </div>
      </div>

      <hr />

      {loading ? (
        <LoadingSpinner />
      ) : notes.length === 0 ? (
        <p className="text-muted mb-0">
          No notes yet. Use this for updates as they come in — a fit note, a
          changed return date — so the request's own reason stays as it was
          written.
        </p>
      ) : (
        <ul className="list-unstyled mb-0">
          {notes.map((note) => (
            <li key={note.Id} className="border-bottom pb-2 mb-2">
              {editingId === note.Id ? (
                <>
                  <Input
                    className="mb-2"
                    value={editDraft.Title}
                    onChange={(e) =>
                      setEditDraft({ ...editDraft, Title: e.target.value })
                    }
                  />
                  <Input
                    type="textarea"
                    rows={2}
                    value={editDraft.Description}
                    onChange={(e) =>
                      setEditDraft({
                        ...editDraft,
                        Description: e.target.value,
                      })
                    }
                  />
                  <div className="d-flex justify-content-end gap-2 mt-2">
                    <Button
                      color="light"
                      size="sm"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="success"
                      size="sm"
                      onClick={handleSaveEdit}
                      disabled={saving}
                    >
                      Save
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="d-flex align-items-start gap-2">
                    <div className="flex-grow-1 min-w-0">
                      {note.Title && (
                        <div className="fw-semibold">{note.Title}</div>
                      )}
                      {note.Description && (
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {note.Description}
                        </div>
                      )}
                      <div className="text-muted small mt-1">
                        {note.Staff_FullName || "Unknown"} ·{" "}
                        {HandleDateTime(note.CreatedDateTime)}
                        {note.UpdatedDateTime &&
                          note.UpdatedDateTime !== note.CreatedDateTime && (
                            <> · edited {HandleDateTime(note.UpdatedDateTime)}</>
                          )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 d-flex gap-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 text-muted"
                        title="Edit this note"
                        onClick={() => {
                          setEditingId(note.Id);
                          setEditDraft({
                            Title: note.Title || "",
                            Description: note.Description || "",
                          });
                        }}
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 text-danger"
                        title="Delete this note"
                        onClick={() => handleDelete(note.Id)}
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LeaveRequestNotes;
